import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  Icon,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Link,
} from '@material-ui/core';
import moment from 'moment';

import Actions from '../../../../../../store/ducks/appointments';
import { useAppointments } from '../../../../context';
import Loading from './Loading';
import useStyles from './styles';

const SlotDetailsModal = ({ onExited, historyId }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    history: { list: listHistory, allLoaded: historyLoaded },
  } = useSelector(state => state.appointments);
  const { getStatusStyle } = useAppointments();

  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [appointmentInfos, setAppointmentInfos] = useState(null);
  const [timeRange, setTimeRange] = useState([]);

  const statusType = useMemo(() => {
    if (!appointmentInfos) return '';
    const { status, userAbsent, userInfoAbsence, removed } = appointmentInfos;
    if (removed) return 'CANCELED';
    if (userAbsent) return 'ABSENT';
    if (userInfoAbsence) return 'INFO_ABSENCE';
    switch (status) {
      case 'HAPPENED':
        return 'CONCLUDED';
      default:
        return 'SCHEDULED';
    }
  }, [appointmentInfos]);

  const statusStyle = useMemo(() => getStatusStyle(statusType), [
    getStatusStyle,
    statusType,
  ]);

  useEffect(() => {
    if (!historyLoaded) {
      dispatch(Actions.requestAppointments());
    }
  }, [dispatch, historyId, historyLoaded]);

  useEffect(() => {
    if (historyId in listHistory) {
      setAppointmentInfos(listHistory[historyId]);
    } else {
      setAppointmentInfos(null);
    }
  }, [historyId, listHistory]);

  useEffect(() => {
    if (appointmentInfos) {
      setTimeRange([appointmentInfos.startTime, appointmentInfos.endTime]);
      setLoading(false);
    } else {
      setTimeRange([]);
      setLoading(true);
    }
  }, [appointmentInfos, historyId]);

  const handleCloseModal = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <Dialog
      className={classes.container}
      classes={{ paper: classes.paper }}
      onClose={handleCloseModal}
      onExited={onExited}
      open={open}
      maxWidth="xs"
    >
      <DialogContent className="slot-details-content">
        {loading ? (
          <Loading />
        ) : (
          <>
            <Box className="slot-details-header">
              <Icon className="slot-details-header-icon" fontSize="inherit">
                check_circle
              </Icon>
              <Box className="slot-details-header-infos">
                <Typography
                  className="slot-details-header-label"
                  variant="overline"
                >
                  Agendamento para:
                </Typography>
                <Typography
                  className="slot-details-header-text"
                  variant="body2"
                >
                  {`${moment(appointmentInfos?.date).format('DD/MM/YYYY')} -
                    ${timeRange[0]} às ${timeRange[1]}`}
                </Typography>
              </Box>
            </Box>
            <Typography
              className="slot-details-listInfos-title"
              variant="overline"
            >
              Detalhes do agendamento:
            </Typography>
            <Box component="ul" className="slot-details-listInfos-container">
              <Box component="li" className="slot-details-listInfos-item">
                <Typography
                  className="slot-details-listInfos-label"
                  variant="caption"
                >
                  Status:
                </Typography>
                <Box className="slot-details-listInfos-value-container">
                  <Typography
                    className={`slot-details-listInfos-status ${statusStyle.className}`}
                    variant="caption"
                  >
                    <Icon className="slot-details-listInfos-status-icon">
                      {statusStyle.icon}
                    </Icon>
                    {statusStyle.label}
                  </Typography>
                </Box>
              </Box>
              <Box component="li" className="slot-details-listInfos-item">
                <Typography
                  className="slot-details-listInfos-label"
                  variant="caption"
                >
                  Profissional:
                </Typography>
                <Box className="slot-details-listInfos-value-container">
                  <Typography
                    className="slot-details-listInfos-value-primary"
                    variant="body2"
                  >
                    {appointmentInfos?.professionalName}
                  </Typography>
                  <Typography
                    className="slot-details-listInfos-value-secondary"
                    variant="caption"
                  >
                    {appointmentInfos?.skypeConsultant}
                  </Typography>
                </Box>
              </Box>
              <Box component="li" className="slot-details-listInfos-item">
                <Typography
                  className="slot-details-listInfos-label"
                  variant="caption"
                >
                  Principal dúvida:
                </Typography>
                <Box className="slot-details-listInfos-value-container">
                  <Typography
                    className="slot-details-listInfos-value-primary"
                    variant="body2"
                  >
                    {appointmentInfos?.historyUserQuest}
                  </Typography>
                </Box>
              </Box>
              <Box component="li" className="slot-details-listInfos-item">
                <Typography
                  className="slot-details-listInfos-label"
                  variant="caption"
                >
                  Contato informado:
                </Typography>
                <Box className="slot-details-listInfos-value-container">
                  <Typography
                    className="slot-details-listInfos-value-primary"
                    variant="body2"
                  >
                    {appointmentInfos?.historyUserSkype}
                  </Typography>
                </Box>
              </Box>
              {statusType === 'SCHEDULED' && (
                <Box component="li" className="slot-details-listInfos-item">
                  <Typography
                    className="slot-details-listInfos-label"
                    variant="caption"
                  >
                    Link da sala:
                  </Typography>
                  <Box className="slot-details-listInfos-value-container">
                    <Link
                      href={appointmentInfos?.skypeConsultant}
                      target="_blank"
                      className="slot-details-listInfos-value-link"
                    >
                      {appointmentInfos?.skypeConsultant}
                    </Link>
                  </Box>
                </Box>
              )}
            </Box>
          </>
        )}
      </DialogContent>
      {!loading && (
        <DialogActions>
          <Button onClick={handleCloseModal}>Fechar</Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default memo(SlotDetailsModal);
